// src/pages/WellFinderReviewPage.jsx
//
// WellFinder adjudication review. A dedicated top-level route rather than a
// panel inside MapView: 3c drives single-record review from the keyboard, and
// those bindings would compete with free map exploration for the same keys. A
// route boundary is also the natural place to mirror the server's org
// restriction.
//
// TASK 2 IS READ-ONLY. Nothing here issues anything but GET. In particular it
// does not claim: POST /queue/claim belongs to 3c's prefetch, and a list that
// claimed on render would take records off other analysts' queues by being
// looked at.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffectiveOrg } from "../hooks/useEffectiveOrg";
import { useAuthStore } from "../stores/useAuthStore";
import { useWfQueueStore } from "../stores/useWfQueueStore";
import QueueRow from "../components/wellfinder/QueueRow";
import QueueFilters from "../components/wellfinder/QueueFilters";
import OperatingAoiPicker from "../components/OperatingAoiPicker";
import { useWellfinderAoiPref } from "../stores/useWellfinderAoiPref";
import { fetchWithAuth } from "../api/fetchWithAuth";
import QueueProgress from "../components/wellfinder/QueueProgress";
import RecordDetail from "../components/wellfinder/RecordDetail";
import ShortcutHelp from "../components/wellfinder/ShortcutHelp";
import { resolveKey, EQUIPMENT_FOR_ACTION } from "../components/wellfinder/adjudicationKeys";
import { useReviewHeartbeat } from "../hooks/useReviewHeartbeat";

// The adjudication tenant. Mirrors DREAMHAMMER_ORG_ID in
// admin-api/lib/wellfinder/adjudication.js.
const DREAMHAMMER_ORG_ID = "815da853-bff7-4198-a09d-3590b55ef39f";

/**
 * THIS CHECK IS UX, NOT ENFORCEMENT.
 *
 * The security boundary is requireAdjudicationAccess in
 * admin-api/routes/wellfinder-adjudication.mjs, which 403s every adjudication
 * route with code 'not_adjudication_org' no matter what this component renders.
 * That gate has a second half this component does NOT mirror: writes also
 * require the wellfinder_reviewer realm role. A DH user without it can read the
 * queue and will be refused at commit, which is the correct place to find out —
 * do not hide the review UI on a role the client cannot verify.
 *
 * What this does is spare a non-DreamHammer user a screen of 403s. It does not
 * protect anything. Do not relax the server check on the grounds that "the UI
 * already handles it": a client gate is a suggestion, and this one is bypassed
 * by typing a URL.
 *
 * useEffectiveOrg returns null while impersonation is mid-load rather than
 * falling back to the real org, so the pending window fails closed — a platform
 * admin switching into a tenant never briefly sees the entry point.
 */
export function useIsAdjudicationOrg() {
  const org = useEffectiveOrg();
  return org?.id === DREAMHAMMER_ORG_ID;
}

function NotAdjudicationOrg({ org }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-bg p-8">
      <div className="max-w-md text-center">
        <h2 className="text-fg-primary text-lg font-medium mb-2">
          Review is not available for this organization
        </h2>
        <p className="text-fg-secondary text-sm leading-relaxed">
          WellFinder detection review is carried out once, by DreamHammer, and the
          results are published to each organization&rsquo;s own records. There is
          nothing to review here.
        </p>
        {org?.name && (
          <p className="text-fg-secondary text-xs mt-3">
            Current organization: {org.name}
          </p>
        )}
      </div>
    </div>
  );
}

// URL <-> store. The route keeps tier/status as comma-joined strings because a
// repeatable query param round-trips badly through validateSearch; the API
// accepts either form.
const splitParam = (v) => (v ? String(v).split(",").filter(Boolean) : []);

export default function WellFinderReviewPage() {
  const org = useEffectiveOrg();
  const isAdjudicationOrg = useIsAdjudicationOrg();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  // The DB user id, not the Keycloak sub. claimed_by is aiprime.users.id and the
  // token's `sub` is a different uuid entirely — comparing against it would make
  // every row the analyst holds render as somebody else's.
  const myUserId = useAuthStore((s) => s.profile?.id ?? null);
  const loadProfile = useAuthStore((s) => s.loadProfile);

  const ids = useWfQueueStore((s) => s.ids);
  const records = useWfQueueStore((s) => s.records);
  const loading = useWfQueueStore((s) => s.loading);
  const error = useWfQueueStore((s) => s.error);
  const hasMore = useWfQueueStore((s) => s.hasMore);
  const filters = useWfQueueStore((s) => s.filters);
  const selectedId = useWfQueueStore((s) => s.selectedId);
  const select = useWfQueueStore((s) => s.select);
  const helpOpen = useWfQueueStore((s) => s.helpOpen);
  const setHelpOpen = useWfQueueStore((s) => s.setHelpOpen);
  const progressExpanded = useWfQueueStore((s) => s.progressExpanded);
  const toggleProgressExpanded = useWfQueueStore((s) => s.toggleProgressExpanded);
  const claimBatch = useWfQueueStore((s) => s.claimBatch);
  // Null unless claimBatch is refusing to claim. Surfaced on the filter row
  // so the analyst learns it where they caused it.
  const claimsPausedReason = useWfQueueStore((s) => s.claimsPausedReason);
  const releaseClaims = useWfQueueStore((s) => s.releaseClaims);
  const moveSelection = useWfQueueStore((s) => s.moveSelection);
  const setFilters = useWfQueueStore((s) => s.setFilters);
  const loadFirstPage = useWfQueueStore((s) => s.loadFirstPage);
  const loadNextPage = useWfQueueStore((s) => s.loadNextPage);
  const stats = useWfQueueStore((s) => s.stats);
  const statsLoading = useWfQueueStore((s) => s.statsLoading);
  const statsError = useWfQueueStore((s) => s.statsError);
  const loadStats = useWfQueueStore((s) => s.loadStats);

  // The URL is the source of truth for filters, so a filtered queue is a
  // shareable link.
  const urlFilters = useMemo(() => {
    const statuses = splitParam(search?.status);
    return {
      tiers: splitParam(search?.tier),
      statuses: statuses.length ? statuses : ["pending"],
      sort: search?.sort ?? "thorough",
      // FOUR PLACES CARRY A FILTER AND ALL FOUR HAVE TO KNOW ABOUT IT: this
      // object, its dependency list, the changed-check below, and the search
      // object onFilterChange writes. Plus validateSearch in router.jsx, which
      // strips anything it does not name. Miss any one and the control renders,
      // accepts a click, and does nothing.
      aoiId: search?.aoi ?? null,
    };
  }, [search?.tier, search?.status, search?.sort, search?.aoi]);

  // Gated on the org for the same reason every other fetch on this page is: a
  // non-DreamHammer user, or one whose impersonation is still resolving, gets a
  // page that explains itself and issues no requests at all.
  useEffect(() => {
    if (!isAdjudicationOrg) return;
    loadProfile?.();
  }, [isAdjudicationOrg, loadProfile]);

  // Stats are loaded once on arrival, and then ONLY when the AOI changes.
  //
  // Not on every filter change, which is the original decision and still right:
  // the header describes the corpus rather than the page, so a tier or status
  // chip does not move a single number on it, and re-fetching would flicker an
  // orientation surface while the analyst is doing something else.
  //
  // The AOI is the exception because it changes WHICH CORPUS. Leaving the header
  // at 10,800 above a list of 4,647 is worse than not filtering at all — the
  // reviewer cannot tell how much of the assigned work is left, which is the
  // only reason to filter. That reload lives in setFilters, gated on the AOI.
  useEffect(() => {
    if (!isAdjudicationOrg) return;
    loadStats();
  }, [isAdjudicationOrg, loadStats]);

  useEffect(() => {
    if (!isAdjudicationOrg) return;
    const { filters: cur, setFilters: apply, loadFirstPage: first } =
      useWfQueueStore.getState();
    const changed =
      urlFilters.sort !== cur.sort ||
      urlFilters.tiers.join(",") !== cur.tiers.join(",") ||
      urlFilters.statuses.join(",") !== cur.statuses.join(",") ||
      urlFilters.aoiId !== cur.aoiId;
    // setFilters resets the working set and loads page one; loadFirstPage does
    // the same without touching filters. Read from getState rather than the
    // subscribed `filters` so this effect is keyed on the URL alone — depending
    // on state it writes would loop.
    if (changed) apply(urlFilters);
    else first();
  }, [isAdjudicationOrg, urlFilters]);

  // ── The AOIs this reviewer may filter by ────────────────────────────────
  //
  // Its own endpoint, not /api/v1/operating-aois: this list carries a
  // record_count per AOI, which is what makes an entry choosable. Loaded once
  // when the page mounts — the counts move only on ingest, and re-fetching on
  // every AOI change would pay 147 ms to redraw numbers that did not change.
  const [aois, setAois] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  // The NAME, for the header. The header's counts are AOI-scoped and the A'
  // calibration beside them is not; QueueProgress puts the scope into the words
  // rather than marking it, so it needs the name and not just the id.
  const areaName = useMemo(
    () => aois.find((a) => a.id === filters.aoiId)?.name ?? null,
    [aois, filters.aoiId],
  );
  useEffect(() => {
    if (!isAdjudicationOrg) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchWithAuth("/api/v1/wellfinder/operating-aois");
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (cancelled) return;
        setAois(Array.isArray(d.operating_aois) ? d.operating_aois : []);
        setTotalRecords(d.total_records ?? 0);
      } catch {
        // The picker degrades to "All areas" only, which is the unfiltered
        // queue — the behaviour before this filter existed. Not worth an error
        // surface of its own.
      }
    })();
    return () => { cancelled = true; };
  }, [isAdjudicationOrg]);

  const onFilterChange = useCallback((patch) => {
    const next = { ...filters, ...patch };
    // Only the URL is written here. The urlFilters effect above notices the
    // change and drives the store, so there is exactly one path from a chip
    // click to a fetch rather than two racing ones.
    navigate({
      to: "/workspace/review",
      search: {
        tier: next.tiers.length ? next.tiers.join(",") : undefined,
        status: next.statuses.join(",") === "pending" ? undefined : next.statuses.join(","),
        sort: next.sort === "thorough" ? undefined : next.sort,
        // undefined, not null — validateSearch drops undefined, so clearing the
        // AOI removes the param rather than leaving ?aoi=null, which would fail
        // the uuid test on the next read and mean the same thing by a longer
        // route.
        //
        // A PLAIN OBJECT, NOT (prev) => ({ ...prev }). Spreading prev here would
        // also carry `record` through a filter change, and the two record
        // effects below have a documented freeze loop around exactly which
        // revision of that id is in the URL. Changing filters intentionally
        // drops the open record; that is existing behaviour and not this
        // filter's to revisit.
        aoi: next.aoiId ?? undefined,
      },
      replace: true,
    });

    // Remember the AOI across sessions. Written on EVERY filter change, not
    // only when the AOI is set: clearing it back to all-AOIs is a choice too,
    // and a store that only records selections would re-apply the old area on
    // the next login after the reviewer deliberately dropped it.
    useWellfinderAoiPref.getState().rememberAoi(next.aoiId ?? null);
  }, [filters, navigate]);

  // ── RESTORE THE LAST AOI, BY SEEDING THE URL ──────────────────────────────
  //
  // The analysts asked for the AOI to survive logoff/login and a closed tab. A
  // refresh already worked, because the AOI is in the URL; nothing else did,
  // because a fresh login lands on a bare /workspace/review.
  //
  // THIS WRITES THE URL AND NOTHING ELSE. Four places already have to agree
  // about a filter (see urlFilters above) and the URL is the one authority; a
  // store that applied the AOI itself would be a fifth, racing the effect that
  // reads the URL. Navigating means the restored AOI travels the exact path a
  // click travels.
  //
  // THE URL WINS. Only fires when the URL carried no ?aoi= on arrival, so a
  // shared deep link — or an explicit ?aoi= — is never overridden by somebody's
  // saved preference.
  //
  // VALIDATED AGAINST THE LOADED LIST, which is why it waits for that fetch. A
  // remembered AOI can be deleted, or fall outside a reviewer's grant; applying
  // it blind would filter the queue to zero records and read as an empty
  // corpus. If it is gone, the preference is dropped rather than retried every
  // time the page opens.
  //
  // ONCE PER MOUNT, and restoredRef is what makes that true rather than the
  // dependency list. This sits below onFilterChange so it can depend on it
  // directly — onFilterChange is rebuilt on every filter change, so without the
  // guard this effect would re-enter on the very change it just caused. The
  // guard is set BEFORE navigating, so re-entry is impossible either way.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!isAdjudicationOrg || restoredRef.current) return;
    if (!aois.length) return;                 // list not in yet — nothing to validate against
    if (search?.aoi) { restoredRef.current = true; return; }   // the URL wins

    const remembered = useWellfinderAoiPref.getState().lastAoiId;
    if (!remembered) { restoredRef.current = true; return; }

    restoredRef.current = true;
    if (!aois.some((a) => a.id === remembered)) {
      // Deleted, or no longer granted. Forget it so this does not run again
      // tomorrow against the same dead id.
      useWellfinderAoiPref.getState().rememberAoi(null);
      return;
    }
    onFilterChange({ aoiId: remembered });
  }, [isAdjudicationOrg, aois, search?.aoi, onFilterChange]);


  // ── URL <-> selection ──────────────────────────────────────────────────────
  //
  // Deep link IN: ?record=<uuid> opens that record directly, whether or not it
  // is on the loaded page of the queue. select() does not need the id to be in
  // `ids` — it sets the selection and fetches the detail by id.
  //
  // THROUGH select(), NOT BESIDE IT. This effect used to set selectedId and
  // call loadDetail itself, which skipped select()'s reset — so geometry, a
  // boundary draft or a note staged on the previous record was still staged
  // when this one opened, and would be committed against it. That is the
  // 2026-09-10 failure (a boundary written ~34 km from where it was drawn) by
  // a second door. select() is the one place that knows what belongs to a
  // record; a second copy of that list here would drift from it.
  //
  // The same-id guard matters: the OUT effect below echoes the open record
  // into the URL, and resetting on that echo would wipe a draft mid-draw.
  // select() carries the guard too; it is repeated here so the intent sits
  // where the echo arrives.
  const urlRecord = search?.record ?? null;

  useEffect(() => {
    if (!isAdjudicationOrg || !urlRecord) return;
    if (useWfQueueStore.getState().selectedId === urlRecord) return;
    select(urlRecord);
  }, [isAdjudicationOrg, urlRecord, select]);

  // Deep link OUT: selecting a row writes the id back. `replace` so advancing
  // through a hundred records does not bury the back button under a hundred
  // history entries.
  useEffect(() => {
    if (!isAdjudicationOrg) return;
    // ── READ THE LIVE VALUE, NOT THE RENDER'S ────────────────────────────────
    //
    // `selectedId` from the closure is the value as of the render that scheduled
    // this effect. The IN effect above runs FIRST in the same flush and may have
    // just replaced it, so the closure value can be one revision stale — and
    // acting on it writes that stale id back into the URL.
    //
    // THE FREEZE THIS CAUSED. selectedId lives in a module-singleton store that
    // outlives the component, so leaving the surface and returning to a
    // DIFFERENT record began the second visit with the previous record still
    // selected. OUT rewrote the URL to that stale id, IN pulled selectedId back
    // to match it, and the two swapped roles indefinitely, firing a navigate and
    // a loadDetail every cycle.
    //
    // reset() does null selectedId, which is why it froze only sometimes: whether
    // OUT saw the stale value depended on whether the reset had landed first.
    //
    // selectedId stays in the dependency list — it is still what should WAKE this
    // effect when the list changes the selection. It is just not what the effect
    // should READ.
    const current = useWfQueueStore.getState().selectedId;
    if (!current || current === urlRecord) return;
    navigate({
      to: "/workspace/review",
      search: (prev) => ({ ...prev, record: selectedId }),
      replace: true,
    });
  }, [isAdjudicationOrg, selectedId, urlRecord, navigate]);

  // Default open. 3c decides whether focused work collapses it; until an
  // analyst is committing verdicts there is no evidence either way, and losing
  // your place in a 10,803-record queue is the more expensive mistake.
  const [listOpen, setListOpen] = useState(true);

  // ── Focus heartbeat ────────────────────────────────────────────────────────
  //
  // Gated on the org for the same reason every other request here is: a
  // non-DreamHammer user gets a page that explains itself and issues nothing.
  //
  // The context is read AT PING TIME through a closure, not captured at mount —
  // the open record changes every few seconds and a captured value would
  // attribute a whole session to the first record.
  useReviewHeartbeat(isAdjudicationOrg, () => {
    const st = useWfQueueStore.getState();
    const rec = st.detail?.record;
    return {
      // Null when no record is open. Time on the queue list — filtering,
      // orienting — is still work and is recorded as such.
      record_id: st.selectedId ?? null,
      tier: rec?.tier ?? st.records[st.selectedId]?.tier ?? null,
    };
  });

  // ── Claims ─────────────────────────────────────────────────────────────────
  //
  // Claim a batch on entry so advance always has somewhere to go, and release
  // on the way out. Release is best effort — the 15-minute TTL is the real
  // guarantee and this failing costs at most that.
  useEffect(() => {
    if (!isAdjudicationOrg) return;
    claimBatch();
    return () => { releaseClaims(); };
  }, [isAdjudicationOrg, claimBatch, releaseClaims]);

  // ── Keyboard ───────────────────────────────────────────────────────────────
  //
  // WINDOW-LEVEL, not on the list container. In 3a the list had focus because
  // the list was the whole page; now the analyst's attention is on the map and
  // the verdict bar, and requiring a click into a listbox before `p` works
  // would be an extra interaction on every record — which is the entire budget.
  //
  // resolveKey refuses while a text input has focus, so typing a note cannot
  // commit five records. See adjudicationKeys.js for the map and the reasoning.
  const listRef = useRef(null);
  useEffect(() => {
    if (!isAdjudicationOrg) return;
    const onKey = (e) => {
      const action = resolveKey(e);
      if (!action) return;
      const st = useWfQueueStore.getState();

      if (action.startsWith("candidate:")) {
        const rank = Number(action.slice(10));
        if (st.detail?.candidates?.some((c) => c.rank === rank)) {
          e.preventDefault();
          st.selectCandidate(rank);
        }
        return;
      }

      switch (action) {
        case "approve_pump_jack":
        case "approve_frac_well":
        case "approve_no_equipment":
          if (!st.detail?.record) return;
          e.preventDefault();
          // Armed by 0: the same key states the equipment of a No Candidate.
          st.commit("approved", st.noCandidateArmed
            ? { noCandidate: true, equipmentType: EQUIPMENT_FOR_ACTION[action] }
            : { equipmentType: EQUIPMENT_FOR_ACTION[action] });
          return;
        case "approve_no_candidate":
          if (!st.detail?.record) return;
          e.preventDefault();
          st.armNoCandidate();
          return;
        case "reject":
          if (!st.detail?.record) return;
          e.preventDefault();
          st.setRejectPickerOpen(!st.rejectPickerOpen);
          return;
        case "flag":
          if (!st.detail?.record) return;
          e.preventDefault();
          st.commit("flagged", {});
          return;
        case "note":
          e.preventDefault();
          window.dispatchEvent(new CustomEvent("wf:focus-note"));
          return;
        case "move":
          e.preventDefault();
          st.setGeomMode(!st.geomMode);
          return;
        case "boundary":
          e.preventDefault();
          // setBoundaryMode refuses when the record's entity holds no boundary
          // link, so the shortcut cannot reach a state the button is disabled
          // for. ONE gate, not two that can disagree.
          st.setBoundaryMode(!st.boundaryMode);
          return;
        case "zoom":
          if (!st.detail?.record) return;
          e.preventDefault();
          st.toggleZoom();
          return;
        case "undo":
          e.preventDefault();
          st.undoLastCommit();
          return;
        case "next": e.preventDefault(); st.moveSelection(1); return;
        case "prev": e.preventDefault(); st.moveSelection(-1); return;
        case "cancel": e.preventDefault(); st.cancelMode(); return;
        case "help": e.preventDefault(); st.setHelpOpen(!st.helpOpen); return;
        default:
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAdjudicationOrg]);

  // Enter on a focused row still selects it. Arrows and j/k are handled at the
  // window so they work without the list having focus.
  const onKeyDown = useCallback((e) => {
    if (e.key === "Enter" && selectedId) { e.preventDefault(); select(selectedId); }
  }, [select, selectedId]);

  // Keep the selected row on screen when the keyboard moves it off.
  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    listRef.current
      .querySelector(`[data-testid="queue-row-${selectedId}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedId]);

  // Pending impersonation resolution — org is null and we cannot yet tell.
  // Render nothing rather than the wrong thing.
  if (org === null) return null;
  if (!isAdjudicationOrg) return <NotAdjudicationOrg org={org} />;

  const empty = !loading && !error && ids.length === 0;

  return (
    <div className="absolute inset-0 flex flex-col bg-bg">
      {/* THE HEADER IS A COST AT EVERY SCROLL POSITION. This block is shrink-0
          inside a flex column whose root has no overflow, so nothing here ever
          scrolls away — it comes off the working panels' height on every
          viewport. The title, the subtitle and a three-column progress grid
          were ~276px of that; the subtitle is decoration and the grid is
          orientation, neither of which is read during a three-second loop. */}
      {/* relative, so the expand control can straddle the rule below; pb-[9px]
          rather than pb-1 — 5px more than before, which is enough to clear the
          chevron without the header reading as a band of its own. The rule is
          the boundary between orientation and work: everything above it is read
          on arrival, everything below it is the loop. */}
      <div className="relative shrink-0 border-b border-bg-border px-4 pt-2 pb-[9px]">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setListOpen((v) => !v)}
            aria-label={listOpen ? "Collapse queue list" : "Expand queue list"}
            aria-expanded={listOpen}
            className="shrink-0 rounded p-1 text-fg-secondary ring-1 ring-white/10 hover:bg-white/[0.06]"
          >
            {listOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
          </button>
          {/* On the same line as the progress strip when collapsed. The
              subtitle is gone: "hardest first" is restated by the order toggle
              two rows down and by the tier badges on every row. */}
          {/* The only way back to the landing used to be the browser's back
              button, which is not a control — it is a guess about history that
              is wrong the moment someone arrived here from a shared
              ?record= link. */}
          <Link
            to="/workspace"
            title="Back to the workspace"
            aria-label="Back to the workspace"
            className="shrink-0 rounded p-1 text-fg-secondary ring-1 ring-white/10 transition-colors hover:bg-white/[0.06] hover:text-fg-primary"
          >
            <ArrowLeft size={14} />
          </Link>
          <h1 className="shrink-0 text-sm font-medium text-fg-primary">WellFinder Review</h1>

          {/* ── THE ASSIGNMENT, BESIDE THE TITLE ────────────────────────────
              Not in the filter row with the tier and status chips, which is
              where it was and where it was missed. Those chips NARROW a queue;
              this says WHICH queue — a reviewer picks it once on sitting down
              and then works inside it, so it reads as part of what this page IS
              rather than as an optional refinement two rows down.

              Always visible, whether progress is expanded or collapsed, because
              the answer to "what am I working on" should never require opening
              a section to see. */}
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
              Area
            </span>
            <OperatingAoiPicker
              aois={aois}
              value={filters.aoiId}
              onChange={(id) => onFilterChange({ aoiId: id })}
              allLabel="All areas"
              /* "4,647 of 10,800". THE DENOMINATOR IS THE POINT: one of the
                 three AOIs contains every record, so "10,800 of 10,800" is the
                 only honest way to show that picking it changes nothing. */
              renderMeta={(a) =>
                typeof a?.record_count === "number"
                  ? `${a.record_count.toLocaleString()} of ${totalRecords.toLocaleString()}`
                  : ""
              }
            />
          </div>

          {!progressExpanded && (
            <div className="min-w-0 flex-1">
              <QueueProgress
                stats={stats} loading={statsLoading} error={statsError}
                expanded={false}
                areaName={areaName}
              />
            </div>
          )}
        </div>
        {progressExpanded && (
          <QueueProgress
            stats={stats} loading={statsLoading} error={statsError}
            expanded
            areaName={areaName}
          />
        )}
        <QueueFilters
          filters={filters}
          onChange={onFilterChange}
          claimsPausedReason={claimsPausedReason}
        />

        {/* ON the rule, centred. Half above the line and half below, so it
            reads as belonging to the boundary rather than to either side —
            which is what it does: it moves the boundary. bg-bg so the rule
            appears to pass behind it rather than through it. */}
        <button
          type="button"
          onClick={toggleProgressExpanded}
          aria-expanded={progressExpanded}
          aria-label={progressExpanded ? "Collapse progress" : "Expand progress"}
          title={progressExpanded ? "Collapse progress" : "Expand progress"}
          data-testid="progress-toggle"
          // border-white/15, not border-bg-border. bg-border is
          // rgba(255,255,255,0.06) — right for a rule you should barely notice,
          // and invisible on a pill sitting against #050711, which left the
          // chevron looking like a floating glyph rather than a control.
          className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2 rounded border border-white/15 bg-bg px-2 py-0.5 text-fg-secondary hover:border-white/25 hover:bg-bg-soft hover:text-fg-primary"
        >
          {progressExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* Two panes. The list stays visible so the analyst keeps their position
          in a 10,803-record queue; the imagery gets the width because the
          judgement is made from it. */}
      <div className="flex min-h-0 flex-1">
        <div
          className={[
            "flex min-h-0 flex-col border-r border-bg-border",
            // 26rem is the target and 24rem the floor. Below ~24rem the three
            // row columns cannot all fit, and the row degrades by truncating
            // rather than by overlapping — but truncating an operator down to
            // three characters is not legibility either, so the pane refuses to
            // go further. At a 1280px window this leaves 896px for the detail
            // pane, which clears the map's 480px floor with room for the
            // candidate column.
            listOpen
              ? "w-[26rem] min-w-[24rem] shrink-0"
              : "w-0 min-w-0 overflow-hidden border-r-0",
          ].join(" ")}
        >
      <div
        ref={listRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="listbox"
        aria-label="Adjudication queue"
        className="flex-1 overflow-auto outline-none focus:ring-1 focus:ring-accent-info/40"
      >
        {ids.map((id) => (
          <QueueRow
            key={id}
            row={records[id]}
            selected={id === selectedId}
            isMine={Boolean(myUserId) && records[id]?.claimed_by === myUserId}
            onSelect={select}
          />
        ))}

        {empty && (
          <div className="px-4 py-8 text-fg-secondary text-sm">
            Nothing pending in this filter.
          </div>
        )}

        {error && (
          <div className="px-4 py-8 text-red-400 text-sm">
            {error.code === "not_adjudication_org"
              ? "Server refused: not the adjudication org."
              : `Error: ${error.message}`}
          </div>
        )}
      </div>

      {/* Explicit load-more, not infinite scroll and not virtualization. The
          analyst's real flow is keyboard advance through a working set, not
          browsing; a list that grows as they scroll makes the working set an
          accident of how far they happened to scroll. */}
      <div className="shrink-0 border-t border-bg-border px-4 py-2 flex items-center gap-4 text-xs text-fg-secondary">
        <span className="shrink-0">loaded: {ids.length}</span>
        {loading && <span>loading…</span>}
        {!loading && hasMore && (
          <button
            type="button"
            onClick={() => loadNextPage("manual")}
            className="rounded px-2 py-0.5 ring-1 ring-white/15 hover:bg-white/[0.06] text-fg-primary"
          >
            Load more
          </button>
        )}
        {!loading && !hasMore && <span>end of queue</span>}
        {/* A uuid is 36 unbreakable characters in a 24rem pane. Unconstrained
            it pushed the load-more button off the row entirely. */}
        <span className="ml-auto min-w-0 truncate" title={selectedId ?? undefined}>
          selected: {selectedId ?? "none"}
        </span>
      </div>
        </div>

        <div className="relative min-w-0 flex-1">
          <RecordDetail />
          <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
        </div>
      </div>
    </div>
  );
}
